import { LinkIcon } from "@heroicons/react/24/outline";
import fetch from "cross-fetch";
import { useCallback, useMemo } from "react";
import { mutate } from "swr";
import useStatusPages from "@/components/context/useStatusPages";
import { StatusPage } from "@/components/models/StatusPage";
import { useRouter } from "next/router";
import { Button } from "@/components/ui/Button";
import { PageHeader } from "@/components/ui/page-header";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/Table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/Badge";
import {
  MoreHorizontal,
  Pencil,
  Trash,
  ExternalLink,
  PlusIcon,
  Globe,
  FileText,
} from "lucide-react";

export default function StatusPagesList() {
  const { statusPages } = useStatusPages();
  const router = useRouter();

  const onEditStatusPage = useCallback(
    (statusPage: StatusPage) => {
      router.push(`/dashboard/status_pages/${statusPage.id}/edit`);
    },
    [router],
  );

  const addStatusPageClicked = useCallback(() => {
    router.push("/dashboard/status_pages/new");
  }, [router]);

  const deleteClicked = useCallback((page: StatusPage) => {
    if (window.confirm(`Delete "${page.name}" status page?`)) {
      fetch(`/web_api/status_pages/${page.id}`, {
        method: "DELETE",
      })
        .then((resp) => resp.json())
        .then((json) => {
          // console.log({ json });

          if (json.status === "ok") {
            mutate("/web_api/status_pages");
            mutate(`/web_api/status_pages/${page.id}`);
          }
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }, []);

  const pageTitle = useMemo(() => {
    if (statusPages.length === 0) {
      return "Status Pages";
    }
    return "Public Status Pages";
  }, [statusPages.length]);

  const pageDescription = useMemo(() => {
    if (statusPages.length === 0) {
      return "Create your first status page to share uptime with customers";
    }
    return `${statusPages.length} status ${statusPages.length === 1 ? "page" : "pages"} configured`;
  }, [statusPages.length]);

  const getStatusPageUrl = useCallback((page: StatusPage) => {
    if (page.custom_domain) {
      return `https://${page.custom_domain}`;
    }
    return `https://status.uptime-monitor.io/${page.id}`;
  }, []);

  const getMonitorCountLabel = useCallback((page: StatusPage) => {
    const count = page.monitors?.length || 0;
    if (count === 0) return "No monitors";
    if (count === 1) return "1 monitor";
    return `${count} monitors`;
  }, []);

  return (
    <div className="space-y-6">
      <PageHeader title={pageTitle} description={pageDescription}>
        <div className="flex items-center gap-2">
          {statusPages.length > 0 && <div className="pulse-lime w-8 h-8" />}
          <Button onClick={addStatusPageClicked}>
            <PlusIcon className="h-4 w-4 mr-2" />
            New Status Page
          </Button>
        </div>
      </PageHeader>

      {/* Status Pages Table */}
      <div className="rounded-md border">
        {statusPages.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <div className="rounded-full bg-muted p-3 mb-4">
              <FileText className="h-6 w-6 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-medium mb-1">No status pages yet</h3>
            <p className="text-muted-foreground text-center max-w-sm">
              Get started by creating your first status page to share your service uptime with
              customers.
            </p>
            <Button onClick={addStatusPageClicked} className="mt-4">
              <PlusIcon className="h-4 w-4 mr-2" />
              Create your first status page
            </Button>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>URL</TableHead>
                <TableHead>Monitors</TableHead>
                <TableHead>Settings</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {statusPages.map((page) => (
                <TableRow key={page.id}>
                  <TableCell className="font-medium">{page.name}</TableCell>
                  <TableCell>
                    <a
                      href={getStatusPageUrl(page)}
                      target="_blank"
                      rel="noreferrer"
                      className="text-primary hover:text-primary/80 flex items-center gap-1 text-sm"
                    >
                      {page.custom_domain ? (
                        <>
                          <Globe className="h-3 w-3" />
                          {page.custom_domain}
                        </>
                      ) : (
                        <>
                          <LinkIcon className="h-3 w-3" />
                          View page
                        </>
                      )}
                    </a>
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary">{getMonitorCountLabel(page)}</Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      {page.basic_auth && (
                        <Badge variant="outline" className="text-xs">
                          Password protected
                        </Badge>
                      )}
                      {page.show_incident_reasons && (
                        <Badge variant="outline" className="text-xs">
                          Shows incidents
                        </Badge>
                      )}
                      <Badge variant="outline" className="text-xs">
                        {page.period}d history
                      </Badge>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreHorizontal className="h-4 w-4" />
                          <span className="sr-only">Open menu</span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-[160px]">
                        <DropdownMenuLabel>Actions</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem asChild>
                          <a
                            href={getStatusPageUrl(page)}
                            target="_blank"
                            rel="noreferrer"
                            className="cursor-pointer"
                          >
                            <ExternalLink className="mr-2 h-4 w-4" />
                            View page
                          </a>
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => onEditStatusPage(page)}>
                          <Pencil className="mr-2 h-4 w-4" />
                          Edit page
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => deleteClicked(page)}
                          className="text-destructive focus:text-destructive"
                        >
                          <Trash className="mr-2 h-4 w-4" />
                          Delete page
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
